import React, { useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useLocation } from "react-router-dom";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import "../Styles/mtpstyle.css"; // Import custom styles
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap"; // Import Reactstrap modal
import CardHeaderSpan from "../../../Utils/CommonComponents/CardHeaderSpan";
import { toast } from "react-toastify"; // Import toast for notifications
import { dynamicImage } from "../../../Utils";
import { Image } from "../../../AbstractElements";

const MTPBigCalendar: React.FC = () => {
  const location = useLocation();
  const { mtpStatus, updated, mtpId } = location.state || {};
  console.log(updated, mtpStatus, "====================updated");

  const [events, setEvents] = useState<any[]>([]); // State for calendar events
  const [days, setDays] = useState<any[]>([]); // State for days data
  const [labels, setLabels] = useState<string>(""); // State for dynamic labels
  const [selectedEvent, setSelectedEvent] = useState<any>(null); // State for the selected event's data
  const [showModal, setShowModal] = useState(false); // State for event modal visibility
  const [modalOpen, setModalOpen] = useState(false); // State for approve/reject modal visibility
  const [actionType, setActionType] = useState<"approve" | "reject">(); // State for action type
  const [item, setItem] = useState<any>(null); // State for the selected MTP item
  const [loader, setLoader] = useState(false); // State for loader
  const [loading, setLoading] = useState(true); // State for API loading
  const serverUrl = localStorage.getItem("serverUrl");

  // Convert "updated" (yyyy-mm) to "yyyy-mm-01" for FullCalendar's initialDate
  const initialDate = `${updated}-01`;
  // https://sfa.nitiapp.com/api/getMtpDayById?mtp_day_id=68177
  // https://sfa.nitiapp.com/api/getMtpByIdV2?mtp_id=2432&last_log_id=0
  // Fetch data for the calendar
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Show loader
      try {
        const url = `${serverUrl}${API.getMtpByIdV2}`;
        const { status, data } = await getData(url, true, {
          mtp_id: mtpId,
          last_log_id: 0,
        });

        if (status === 200) {
          const calendarEvents: any[] = [];

          // Add holidays
          data?.data?.holiday?.forEach(
            (holiday: { HolidayDate: string; HolidayName: string }) => {
              calendarEvents.push({
                title: `${holiday.HolidayName}`,
                date: holiday.HolidayDate,
                backgroundColor: "#30c9f7", // Blue for holidays
                textColor: "#fff",
                type: "Holiday",
                details: holiday,
              });
            }
          );

          // Add week-offs
          data?.data?.weekOff?.forEach((weekOffDate: string) => {
            calendarEvents.push({
              title: "Week Off",
              date: weekOffDate,
              backgroundColor: "#9e059e", // Purple for week-offs
              textColor: "#fff",
              type: "Week Off",
            });
          });

          // Add leaves
          data?.data?.leaves?.forEach(
            (leave: {
              LeaveStatus: number;
              LeaveDate: string;
              LeaveType: string;
              LeaveRemark: string;
            }) => {
              const leaveStatusText =
                leave.LeaveStatus === 1
                  ? "Raised"
                  : leave.LeaveStatus === 2
                  ? "Approved"
                  : leave.LeaveStatus === 3
                  ? "Rejected"
                  : leave.LeaveStatus === 4
                  ? "Canceled"
                  : "Unknown";

              calendarEvents.push({
                title: `${leave.LeaveType}:${leaveStatusText}`,
                date: leave.LeaveDate,
                backgroundColor: "orange", // Orange for leaves
                textColor: "#fff",
                type: "Leave",
                details: leave,
              });
            }
          );

          // Add agenda days with Coverage counts
          data?.data?.days?.forEach((day: any) => {
            // First event: Coverage counts
            const coverageCounts = day.Coverage.map((c: any) => c.count).join(
              "/"
            );
            calendarEvents.push({
              title: coverageCounts, // e.g., "8/0/15"
              date: day.MtpDayDate,
              backgroundColor: "#11bd5c", // Green for Coverage
              textColor: "#fff",
              type: "Coverage",
              details: day,
            });

            // Second event: NCA (only if NCA is not zero)
            if (day.NCA !== 0) {
              calendarEvents.push({
                title: `NCA: ${day.NCA}`, // e.g., "NCA: 1"
                date: day.MtpDayDate,
                backgroundColor: "red", // Red for NCA
                textColor: "#fff",
                type: "NCA",
                details: day,
              });
            }
          });

          setEvents(calendarEvents); // Update state with events
          setDays(data?.data?.days || []); // Store the days data

          // Set dynamic labels
          setLabels(data?.data?.mtp?.Labels || "");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false); // Hide loader
      }
    };

    fetchData();
  }, [mtpId, serverUrl]);

  // Handle event click
  const handleEventClick = async (info: any) => {
    const clickedEvent = info.event.extendedProps; // Get the extended properties of the clicked event

    if (clickedEvent.details?.MtpDayId) {
      try {
        const url = `${serverUrl}api/getMtpDayById`;
        const { status, data } = await getData(url, true, {
          mtp_day_id: clickedEvent.details.MtpDayId,
        });

        if (status === 200 && data?.data?.MtpDay) {
          const tableData = data.data.MtpDay.map((day: any) => ({
            OutletName: day.OutletName,
            TpDate: new Date(day.TpDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            TpRemark: day.TpRemark || "-",
          }));

          setSelectedEvent({
            type: clickedEvent.type,
            tableData,
          });
          setShowModal(true); // Show the modal
        } else {
          toast.error("Failed to fetch MTP Day details.");
        }
      } catch (error) {
        console.error("Error fetching MTP Day details:", error);
        toast.error("An error occurred while fetching MTP Day details.");
      }
    } else {
      toast.error("No MTP Day ID found for the selected event.");
    }
  };

  // Change MTP status (Approve/Reject)
  const changeMtpStatus = async (action: "approve" | "reject") => {
    setLoader(true);
    try {
      const url = `${serverUrl}${
        action === "approve" ? API.approveMTP : API.rejectMTP
      }`;
      const params = { mtp_id: item?.MtpId };

      const { status, data } = await getData(url, true, params);

      if (status === 200) {
        toast.success(data.message || "MTP Status Updated Successfully!");
      } else {
        toast.error(data.message || "Failed to change MTP status.");
      }
    } catch (error) {
      console.error("Error changing MTP status:", error);
    } finally {
      setLoader(false);
      setModalOpen(false);
    }
  };

  return (
    <div className="page-body">
      <CardHeaderSpan
        headingClassName="py-2"
        back={true}
        heading={"MTP Calendar"}
        span={"ReportListTitle"}
      />

      {loading ? (
        <div className="text-center py-3">
          <div>
            <Image
              className="b-r-10 img-80"
              src={dynamicImage("dashboard/loader.gif")}
              alt="Loading..."
            />
          </div>
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="legend-container">
            <div className="legend-item">
              <div
                className="legend-box"
                style={{ backgroundColor: "#30c9f7" }}
              ></div>
              <span>Holiday</span>
            </div>
            <div className="legend-item">
              <div
                className="legend-box"
                style={{ backgroundColor: "#9e059e" }}
              ></div>
              <span>Week Off</span>
            </div>
            <div className="legend-item">
              <div
                className="legend-box"
                style={{ backgroundColor: "orange" }}
              ></div>
              <span>Leave</span>
            </div>
            <div className="legend-item">
              <div
                className="legend-box"
                style={{ backgroundColor: "red" }}
              ></div>
              <span>NCA</span>
            </div>
            <div className="legend-item">
              <div
                className="legend-box"
                style={{ backgroundColor: "#11bd5c" }}
              ></div>
              <span>{labels.replace(/,/g, " / ")}</span>
            </div>
            {/* Approve/Reject Buttons */}
            {mtpStatus == "requested" && (
              <div className="action-buttons">
                <Button
                  color="success"
                  className="me-2"
                  onClick={() => {
                    setActionType("approve");
                    setItem({ MtpId: mtpId });
                    setModalOpen(true);
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    setActionType("reject");
                    setItem({ MtpId: mtpId });
                    setModalOpen(true);
                  }}
                >
                  Reject
                </Button>
              </div>
            )}
          </div>

          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={initialDate} // Dynamically set the initial date
            events={events} // Pass events to the calendar
            eventDisplay="block"
            height="auto"
            headerToolbar={{
              left: "", // Remove navigation buttons
              center: "title", // Only show the title
              right: "", // Remove view options
            }}
            eventClick={handleEventClick} // Handle event clicks
          />
        </>
      )}

      {/* Modal for Approve/Reject */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Confirm Action
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to {actionType} this MTP?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="primary"
              onClick={() => changeMtpStatus(actionType!)}
            >
              Yes
            </Button>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              No
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Modal for Inner Cell Data */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
        <ModalHeader toggle={() => setShowModal(false)}>
          Event Details
        </ModalHeader>
        <ModalBody>
          {selectedEvent?.tableData ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Outlet Name</th>
                  <th>Tp Date</th>
                  <th>Tp Remark</th>
                </tr>
              </thead>
              <tbody>
                {selectedEvent.tableData.map((row: any, index: number) => (
                  <tr key={index}>
                    <td>{row.OutletName}</td>
                    <td>{row.TpDate}</td>
                    <td>{row.TpRemark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No data available for this event.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MTPBigCalendar;
